import createClient from "openapi-fetch";
import {
  Component,
  createEffect,
  createMemo,
  For,
  onMount,
  Show,
  VoidComponent,
} from "solid-js";
import { render } from "solid-js/web";
import { components, paths } from "./api";
import { createStore, unwrap } from "solid-js/store";
import {
  getCelcius,
  getcmHeightInterval,
  getmmHeightInterval,
} from "./helpers";
import { DateTime, Duration } from "luxon";

import * as L from "leaflet";

import Chart, {
  ChartDataset,
  ChartTypeRegistry,
  ScaleOptionsByType,
} from "chart.js/auto";
import "chartjs-adapter-luxon";
import { LatLngTuple } from "leaflet";

export function doInitLocationDetailed(id: number) {
  render(
    () => <Location id={id} />,
    document.querySelector(".location-detailed"),
  );
}

const client = createClient<paths>({ baseUrl: "/" });

enum PlotPropertyId {
  BAROMETRIC_PRESSURE,
  HUMIDITY,
  RAIN_LASTHOUR,
  SNOW_LASTHOUR,
  SUN_IRRADIANCE,
  TEMPERATURE,
  AVERAGE_WIND,
  WIND_DIRECTION,
}

const radialPlotIds = [PlotPropertyId.WIND_DIRECTION];

const plotProperties = [
  {
    name: "Barometric Pressure",
    id: PlotPropertyId.BAROMETRIC_PRESSURE,
  },
  {
    name: "Humidity",
    id: PlotPropertyId.HUMIDITY,
  },
  {
    name: "Rain last hour",
    id: PlotPropertyId.RAIN_LASTHOUR,
  },
  {
    name: "Snow last hour",
    id: PlotPropertyId.SNOW_LASTHOUR,
  },
  {
    name: "Sun irradiance",
    id: PlotPropertyId.SUN_IRRADIANCE,
  },
  {
    name: "Temperature",
    id: PlotPropertyId.TEMPERATURE,
  },
  {
    name: "Average Wind",
    id: PlotPropertyId.AVERAGE_WIND,
  },
  {
    name: "Wind Direction",
    id: PlotPropertyId.WIND_DIRECTION,
  },
];

enum DTPresetType {
  LAST,
  MOVE,
}
const presets = [
  {
    type: DTPresetType.LAST,
    sec: 3600,
    name: "1 hour",
  },
  {
    type: DTPresetType.LAST,
    sec: 3600 * 12,
    name: "12 hours",
  },
  {
    type: DTPresetType.LAST,
    sec: 3600 * 24,
    name: "24 hours",
  },
  {
    type: DTPresetType.MOVE,
    sec: -3600,
    name: "-1 hour",
  },
  {
    type: DTPresetType.MOVE,
    sec: 3600,
    name: "+1 hour",
  },

  {
    type: DTPresetType.MOVE,
    sec: -3600 * 24,
    name: "-1 day",
  },
  {
    type: DTPresetType.MOVE,
    sec: 3600 * 24,
    name: "+1 day",
  },
];

const [locStore, setLocStore] = createStore({
  latest: undefined,
  plotProps: new Set([
    PlotPropertyId.BAROMETRIC_PRESSURE,
    PlotPropertyId.TEMPERATURE,
    PlotPropertyId.WIND_DIRECTION,
  ]),
  availablePlotProps: new Set(),
  drawError: "",
} as {
  idLocation: number;
  latest: undefined | components["schemas"]["HttpResponseLatestItem"];
  plotProps: Set<PlotPropertyId>;
  availablePlotProps: Set<PlotPropertyId>;
  plotFromDate: string;
  plotFromTime: string;
  plotUntilDate: string;
  plotUntilTime: string;
  // iso formatted UTC DateTime
  plotFromDT: string;
  // iso formatted UTC DateTime
  plotUntilDT: string;
  plotLineDiv: HTMLDivElement;
  plotLineCanvas: HTMLCanvasElement;
  plotRadialDiv: HTMLDivElement;
  plotRadialCanvas: HTMLCanvasElement;
  map: L.Map;
  mapDiv: HTMLDivElement;
  mapLinesGroup: L.LayerGroup;
  lineChart: Chart;
  drawError: string;
});

const Location: Component<{ id: number }> = (params) => {
  onMount(async () => {
    const d = await client.GET("/api/latest", {
      params: {
        query: {
          id_location: params.id,
        },
      },
    });

    if (d.data.list.length != 1) {
      alert("error getting data. refresh now or a bit later.");
      return;
    }
    d.data.list[0].pictures.sort(
      (a, b) => a.direction.Degrees - b.direction.Degrees,
    );
    let ll = d.data.list[0];
    setLocStore("latest", d.data.list[0]);
    let availableProps = new Set<PlotPropertyId>();
    if (ll.wind) availableProps.add(PlotPropertyId.AVERAGE_WIND);
    if (ll.wind) availableProps.add(PlotPropertyId.WIND_DIRECTION);
    if (ll.barometric_pressure)
      availableProps.add(PlotPropertyId.BAROMETRIC_PRESSURE);
    if (ll.humidity) availableProps.add(PlotPropertyId.HUMIDITY);
    if (ll.rain) availableProps.add(PlotPropertyId.RAIN_LASTHOUR);
    if (ll.snow) availableProps.add(PlotPropertyId.SNOW_LASTHOUR);
    if (ll.sun) availableProps.add(PlotPropertyId.SUN_IRRADIANCE);
    if (ll.temperature) availableProps.add(PlotPropertyId.TEMPERATURE);

    setLocStore({
      availablePlotProps: availableProps,
      idLocation: params.id,
      plotFromDate: DateTime.now().minus({ hours: 6 }).toFormat("yyyy-MM-dd"),
      plotFromTime: DateTime.now().minus({ hours: 6 }).toFormat("HH:mm"),
      plotUntilDate: DateTime.now().toFormat("yyyy-MM-dd"),
      plotUntilTime: DateTime.now().toFormat("HH:mm"),
    });

    setTimeout(() => {
      refreshWeatherGraphs();
    }, 1);
  });

  createEffect(() => {
    if (!locStore.plotFromDate || !locStore.plotUntilDate) {
      return;
    }
    let fromDateTime = DateTime.fromFormat(
      `${locStore.plotFromDate} ${locStore.plotFromTime}`,
      "yyyy-MM-dd HH:mm",
    );
    let untilDateTime = DateTime.fromFormat(
      `${locStore.plotUntilDate} ${locStore.plotUntilTime}`,
      "yyyy-MM-dd HH:mm",
    );
    if (fromDateTime.isValid && untilDateTime.isValid) {
      setLocStore({
        plotFromDT: fromDateTime.toISO(),
        plotUntilDT: untilDateTime.toISO(),
      });
      refreshWeatherGraphs();
    } else {
      alert("bad from/until date");
    }
  });

  return (
    <div>
      <Show when={locStore.latest} fallback={<div>loading</div>}>
        {(x) => {
          let ll = locStore.latest;

          return (
            <>
              <a
                href={`https://maps.google.com/?q=${ll.location_xy?.LatLon[0]},${ll.location_xy?.LatLon[1]}`}
                target="_blank"
              >
                open location on Google Maps 🔗
              </a>

              <div class="table-wrapper">
                <table class="table mt-3">
                  <tbody>
                    <Show when={ll.altitude}>
                      <tr>
                        <td class="nowrap" style={{ width: 0 }}>
                          Altitude
                        </td>
                        <td>{ll.altitude.Meters} m</td>
                      </tr>
                    </Show>

                    <Show when={ll.barometric_pressure}>
                      <tr>
                        <td class="nowrap">Barometric Pressure</td>
                        <td>{ll.barometric_pressure} hPa</td>
                      </tr>
                    </Show>

                    <Show when={ll.humidity}>
                      <tr>
                        <td class="nowrap">Humidity</td>
                        <td>{ll.humidity.Relative}%</td>
                      </tr>
                    </Show>

                    <Show when={ll.rain}>
                      <tr>
                        <td class="nowrap">Rain last hour</td>
                        <td>{getmmHeightInterval(ll.rain.ammount_1h)} mm</td>
                      </tr>
                    </Show>

                    <Show when={ll.snow}>
                      <tr>
                        <td class="nowrap">Snow last hour</td>
                        <td>{getcmHeightInterval(ll.snow.current)} cm</td>
                      </tr>
                    </Show>

                    <Show when={ll.sun}>
                      <tr>
                        <td class="nowrap">Sun irradiance</td>
                        <td>{ll.sun.irradiance} W/m²</td>
                      </tr>
                    </Show>

                    <Show when={ll.temperature}>
                      <tr>
                        <td class="nowrap">Temperature</td>
                        <td>{getCelcius(ll.temperature)} °C</td>
                      </tr>
                    </Show>

                    <Show when={ll.wind}>
                      <tr>
                        <td class="nowrap">Average Wind</td>
                        <td>
                          {ll.wind.direction.Degrees}° {ll.wind.speed.Kph} km/h
                        </td>
                      </tr>
                    </Show>
                  </tbody>
                </table>
              </div>

              <h3>Plot</h3>

              <div class="plot-select">
                <For
                  each={plotProperties.filter((x) =>
                    locStore.availablePlotProps.has(x.id),
                  )}
                >
                  {(pp) => {
                    let isChecked = createMemo(() => {
                      return locStore.plotProps.has(pp.id);
                    });
                    return (
                      <div class="plot-select__p">
                        <input
                          type="checkbox"
                          checked={isChecked()}
                          id={`pp-${pp.id}`}
                          onChange={(e) => {
                            if (isChecked()) {
                              setLocStore("plotProps", (prev) => {
                                let ret = new Set(prev);
                                ret.delete(pp.id);
                                return ret;
                              });
                            } else {
                              setLocStore("plotProps", (prev) => {
                                let ret = new Set(prev);
                                ret.add(pp.id);
                                return ret;
                              });
                            }
                            refreshWeatherGraphs();
                          }}
                        />
                        <label for={`pp-${pp.id}`}>{pp.name}</label>
                      </div>
                    );
                  }}
                </For>
              </div>

              <div class="plot-dtrange">
                <div class="plot-dtrange__from">
                  from{" "}
                  <input
                    class="plot-dtrange__date"
                    type="date"
                    value={locStore.plotFromDate}
                    onchange={(e) => {
                      setLocStore("plotFromDate", e.target.value);
                    }}
                  />
                  <input
                    type="time"
                    class="plot-dtrange__time"
                    value={locStore.plotFromTime}
                    onchange={(e) => {
                      setLocStore("plotFromTime", e.target.value);
                    }}
                  />
                </div>
                <div class="plot-dtrange__to">
                  until{" "}
                  <input
                    type="date"
                    class="plot-dtrange__date"
                    value={locStore.plotUntilDate}
                    onchange={(e) => {
                      setLocStore("plotUntilDate", e.target.value);
                    }}
                  />
                  <input
                    type="time"
                    class="plot-dtrange__time"
                    value={locStore.plotUntilTime}
                    onchange={(e) => {
                      setLocStore("plotUntilTime", e.target.value);
                    }}
                  />
                </div>
              </div>

              <div class="plot-presets">
                <For each={presets}>
                  {(p) => {
                    return (
                      <div
                        class="plot-presets__preset btn"
                        onclick={() => {
                          if (p.type == DTPresetType.LAST) {
                            setLocStore({
                              plotFromDate: DateTime.now()
                                .minus({ seconds: p.sec })
                                .toFormat("yyyy-MM-dd"),
                              plotFromTime: DateTime.now()
                                .minus({ seconds: p.sec })
                                .toFormat("HH:mm"),
                              plotUntilDate:
                                DateTime.now().toFormat("yyyy-MM-dd"),
                              plotUntilTime: DateTime.now().toFormat("HH:mm"),
                            });
                          } else if (p.type == DTPresetType.MOVE) {
                            let newDTFrom = DateTime.fromISO(
                              locStore.plotFromDT,
                            ).plus({ seconds: p.sec });
                            let newDTUntil = DateTime.fromISO(
                              locStore.plotUntilDT,
                            ).plus({ seconds: p.sec });
                            setLocStore({
                              plotFromDate: newDTFrom.toFormat("yyyy-MM-dd"),
                              plotFromTime: newDTFrom.toFormat("HH:mm"),
                              plotUntilDate: newDTUntil.toFormat("yyyy-MM-dd"),
                              plotUntilTime: newDTUntil.toFormat("HH:mm"),
                            });
                          }
                        }}
                      >
                        {p.name}
                      </div>
                    );
                  }}
                </For>
              </div>

              <div
                class="btn"
                onclick={() => {
                  refreshWeatherGraphs();
                }}
              >
                show/refresh
              </div>

              <Show when={locStore.drawError != ""}>
                Error: {locStore.drawError}
              </Show>

              <div
                ref={(e) => {
                  setLocStore("plotLineDiv", e);
                }}
                class="plot plot__line"
              >
                <canvas
                  ref={(e) => {
                    setLocStore("plotLineCanvas", e);
                  }}
                ></canvas>
              </div>
              <div class="map" ref={(el) => setLocStore("mapDiv", el)}></div>

              {/*<div ref={(el) => setLocStore("plotRadialDiv", el)}>
                <canvas
                  width={300}
                  height={300}
                  ref={(el) => setLocStore("plotRadialCanvas", el)}
                ></canvas>
              </div>*/}

              <Show when={ll.pictures.length}>
                <div class="loc-photos">
                  <h3>Photos from location</h3>
                  <For each={ll.pictures}>
                    {(p) => {
                      return <img class="img-auto" src={p.url} alt="" />;
                    }}
                  </For>
                </div>
              </Show>
            </>
          );
        }}
      </Show>
    </div>
  );
};

const refreshWeatherGraphs = async () => {
  let ddiff = DateTime.fromISO(locStore.plotUntilDT).diff(
    DateTime.fromISO(locStore.plotFromDT),
  );

  if (ddiff.as("days") > 35) {
    setLocStore("drawError", "Cannot draw more than 35 days");
    return;
  }
  if (locStore.plotProps.size == 0) {
    setLocStore("drawError", "Select at least one thing to draw");
    return;
  }

  setLocStore("drawError", "");

  const d = await client.GET("/api/find", {
    params: {
      query: {
        location: locStore.idLocation,
        dt_from: locStore.plotFromDT,
        dt_until: locStore.plotUntilDT,
        include_pictures: false,
      },
    },
  });

  if (!d.data || d.data.list.length == 0) {
    return;
  }

  d.data.list.sort((a, b) => {
    return a.dt - b.dt;
  });

  if (locStore.plotProps.has(PlotPropertyId.WIND_DIRECTION)) {
    drawMap(d.data.list);
    locStore.mapDiv.style.display = "block";
    // locStore.plotRadialDiv.style.display = "block";
    // drawRadialMy(d.data.list);
  } else {
    // locStore.plotRadialDiv.style.display = "none";
    locStore.mapDiv.style.display = "none";
  }
  {
    let showLineChart = false;
    // line chart
    if (!locStore.lineChart) {
      let chart = new Chart(locStore.plotLineCanvas, {
        type: "line",
        options: {
          animation: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            tooltip: {
              mode: "index",
              intersect: false,
            },
          },
        },
        data: {
          labels: [],
          datasets: [],
        },
      });
      setLocStore("lineChart", chart);
    }

    if (d.data.list.length > 100) {
      locStore.lineChart.options.elements.point = {
        radius: 0,
        hoverRadius: 0,
      };
    } else {
      locStore.lineChart.options.elements.point = {
        radius: 5,
        hoverRadius: 10,
      };
    }

    locStore.lineChart.data.labels = d.data.list.map((_x) => {
      return DateTime.fromMillis(_x.dt);
      // return _x.dt;
    });

    let timeformat = "T";
    let scales = {};
    if (ddiff.as("hours") >= 24) {
      timeformat = "D T";
      scales = {
        x: {
          type: "time",
          time: { unit: "day", displayFormats: { day: "dd LLL" } },
        },
      };
    } else {
      scales = {
        x: {
          type: "time",
          time: { unit: "hour", displayFormats: { hour: "dd LLL" } },
        },
      };
    }

    for (let ppid of Array.from(locStore.plotProps).filter(
      (x) => radialPlotIds.indexOf(x) == -1,
    )) {
      let ppidstr = ppid.toString();
      scales[ppid] = {
        type: "linear",
        position: "left",
      };
    }
    locStore.lineChart.options.scales = scales;
    locStore.lineChart.data.datasets = Array.from(locStore.plotProps)
      .filter((x) => radialPlotIds.indexOf(x) == -1)
      .map((ppid) => {
        showLineChart = true;
        return {
          label: plotProperties.find((x) => x.id == ppid).name,
          yAxisID: ppid.toString(),
          data: d.data.list.map((x) => {
            // return getCelcius(x.temperature);
            switch (ppid) {
              case PlotPropertyId.AVERAGE_WIND:
                return x.wind?.speed?.Kph ?? 0;
              case PlotPropertyId.BAROMETRIC_PRESSURE:
                return x.barometric_pressure;
              case PlotPropertyId.HUMIDITY:
                return x.humidity.Relative;
              case PlotPropertyId.RAIN_LASTHOUR:
                return getmmHeightInterval(x.rain.ammount_1h);
              case PlotPropertyId.SNOW_LASTHOUR:
                return getcmHeightInterval(x.snow.current);
              case PlotPropertyId.SUN_IRRADIANCE:
                return x.sun.irradiance;
              case PlotPropertyId.TEMPERATURE:
                return getCelcius(x.temperature);
            }
          }),
        } as ChartDataset;
      });
    locStore.lineChart.update();
    if (showLineChart) {
      locStore.plotLineDiv.style.display = "block";
    } else {
      locStore.plotLineDiv.style.display = "none";
    }
  }
};

const rad = (deg) => (deg * Math.PI) / 180;
async function drawRadialMy(
  d: components["schemas"]["HttpResponseFindItem"][],
) {
  let canvas = locStore.plotRadialCanvas;
  let cw = canvas.width;
  let ch = canvas.height;
  let ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  let maxwindspeed = Math.max(...d.map((x) => x?.wind?.speed?.Kph ?? 0));

  // draw X axis 0 0
  // draw Y axis 0 0
  //
  let pXC = cw / 2; // center points
  let pYC = ch / 2;
  let maxhalf = Math.min(pXC, pYC);

  ctx.strokeStyle = "#c3c3c3";
  ctx.beginPath();
  ctx.moveTo(0, pYC);
  ctx.lineTo(cw, pYC);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(pXC, 0);
  ctx.lineTo(pXC, ch);
  ctx.stroke();

  ctx.strokeStyle = "#555555";
  let sleepms = 10;
  if (d.length > 500) {
    sleepms = 0;
  } else if (d.length > 200) {
    sleepms = 2;
  } else if (d.length > 50) {
    sleepms = 5;
  }
  for (let dd of d) {
    if (!dd.wind) continue;
    let windspeedratio = dd.wind.speed.Kph / maxwindspeed;
    let px =
      Math.sin(rad(dd.wind.direction.Degrees)) * windspeedratio * maxhalf;
    let py =
      Math.cos(rad(dd.wind.direction.Degrees)) * windspeedratio * maxhalf;

    ctx.beginPath();
    ctx.moveTo(pXC, pYC);
    ctx.lineTo(pXC + px, pYC + py);
    ctx.stroke();
    if (sleepms > 0) {
      await sleep(sleepms);
    }
  }
}

function drawMap(d: components["schemas"]["HttpResponseFindItem"][]) {
  let mapCenter = [
    locStore.latest.location_xy.LatLon[0],
    locStore.latest.location_xy.LatLon[1],
  ];
  if (!locStore.map) {
    let map = L.map(locStore.mapDiv, {}).setView(mapCenter as LatLngTuple, 11);
    L.tileLayer("http://{s}.tile.osm.org/{z}/{x}/{y}.png", {}).addTo(map);

    setLocStore("map", map);

    const group = L.layerGroup([]);
    group.addTo(map);

    setLocStore("mapLinesGroup", group);
  }
  locStore.mapLinesGroup.clearLayers();
  let maxMapSizeDegrees = 0.1;

  let maxwindspeed = Math.max(...d.map((x) => x?.wind?.speed?.Kph ?? 0));

  for (let dd of d) {
    if (!dd.wind) continue;
    let windspeedratio = dd.wind.speed.Kph / maxwindspeed;
    let px =
      Math.sin(rad(dd.wind.direction.Degrees)) *
      windspeedratio *
      maxMapSizeDegrees;
    let py =
      Math.cos(rad(dd.wind.direction.Degrees)) *
      windspeedratio *
      maxMapSizeDegrees;

    var firstpolyline = new L.Polyline(
      [
        new L.LatLng(mapCenter[0], mapCenter[1]),
        new L.LatLng(mapCenter[0] + py, mapCenter[1] + px),
      ],
      {
        color: "red",
        weight: 1,
        opacity: 1,
        smoothFactor: 0,
      },
    );
    firstpolyline.addTo(locStore.mapLinesGroup);
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
